import styled from "styled-components";
import {fontFamilies, fontWeights, radii} from "theme/tokens";
import {eyebrowTextCss} from "theme/typography";
import {advertisingMeasure} from "./AdvertisingContent.styles";

/**
 * Externalized styles for {@link ./AdvertisingCloser} (ui#1156, extended in ui#1861) — the last band,
 * and the fourteen-day availability row beside it.
 */

/**
 * The closing band.
 *
 * Given a raised background and a rule above it so it reads as the page's last word rather than as one
 * more section.
 *
 * `card2`, not `card`. In LIGHT mode `card` and `paper` are both `#ffffff`, so a `card` fill against
 * this page's paper background is no fill at all and the band was a 1px hairline — the intent above
 * happening in dark mode only. `card2` is the token that differs from paper in both modes.
 */
export const CloserBand = styled.section`
	margin-top: 80px;
	border-top: 1px solid ${({theme}) => theme.tokens.line};
	background: ${({theme}) => theme.tokens.card2};
	padding: 64px 0 80px;

	@media (max-width: 640px) {
		margin-top: 56px;
		padding: 48px 0 56px;
	}
`;

/**
 * The promise beside the row.
 *
 * 6:5 rather than even halves — the paragraph needs the wider column, and the row is a fixed 14 cells
 * whose readability stops improving past a certain width.
 */
export const CloserGrid = styled.div`
	${advertisingMeasure};
	display: grid;
	grid-template-columns: minmax(0, 6fr) minmax(0, 5fr);
	gap: 40px;
	align-items: center;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
		gap: 32px;
	}
`;

/**
 * The closer's heading.
 *
 * Sized between the hero's display type and the body: it has to land as a conclusion without competing
 * with the `<h1>` that opens the page.
 */
export const CloserHeading = styled.h2`
	margin: 0;
	font-family: ${fontFamilies.displaySerif};
	font-weight: ${fontWeights.display};
	font-size: clamp(28px, 3.6vw, 44px);
	line-height: 1.08;
	letter-spacing: -0.015em;
	color: ${({theme}) => theme.tokens.ink};
`;

export const CloserParagraph = styled.p`
	margin: 16px 0 0;
	max-width: 36em;
	font-size: 15.5px;
	line-height: 1.68;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** The availability card. */
export const AvailabilityCard = styled.aside`
	padding: 18px 20px 20px;
	border-radius: ${radii.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	background: ${({theme}) => theme.tokens.card};
	box-shadow: ${({theme}) => theme.tokens.shadowCard};
`;

export const AvailabilityHead = styled.div`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
`;

export const AvailabilityTitle = styled.h3`
	margin: 0;
	font-size: 15px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink};
`;

export const AvailabilityLiveTag = styled.span`
	${eyebrowTextCss};
	flex: none;
	font-size: 10.5px;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * Fourteen cells across.
 *
 * A fixed 14 columns rather than `auto-fit`: the row IS the next fourteen days, and a grid that reflowed
 * to two rows of seven on a narrow screen would silently become a week-by-week calendar with no weekday
 * headings. `minmax(0, 1fr)` lets them shrink instead, and the day numbers stay legible down to 390px
 * because the card's own column is capped by the grid above it.
 */
export const AvailabilityDays = styled.ul`
	display: grid;
	grid-template-columns: repeat(14, minmax(0, 1fr));
	gap: 6px;
	margin: 14px 0 0;
	padding: 0;
	list-style: none;
`;

/**
 * One day.
 *
 * The two states are distinguished by THREE things at once — fill, border colour and a strike through
 * the number — never by colour alone. A row where free and taken differed only in hue would be two
 * identical rows to a reader with a colour vision deficiency, and this row is the whole reason the band
 * exists.
 */
export const AvailabilityDay = styled.li<{$free: boolean}>`
	display: grid;
	place-items: center;
	aspect-ratio: 1;
	border-radius: ${radii.chip};
	border: 1px solid ${({theme, $free}) => ($free ? theme.tokens.teal : theme.tokens.cardBorder)};
	background: ${({theme, $free}) => ($free ? theme.tokens.signalTint : theme.tokens.chip)};
	color: ${({theme, $free}) => ($free ? theme.tokens.ink : theme.tokens.ink3)};
	font-size: 11px;
	font-variant-numeric: tabular-nums;
	text-decoration: ${({$free}) => ($free ? "none" : "line-through")};
`;

export const AvailabilityLegend = styled.p`
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
	margin: 12px 0 0;
	font-size: 12.5px;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const AvailabilityLegendItem = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 6px;
`;

/** The legend's swatch: the same fill and border a cell in that state carries. */
export const AvailabilityLegendSwatch = styled.span<{$free: boolean}>`
	width: 10px;
	height: 10px;
	border-radius: 3px;
	border: 1px solid ${({theme, $free}) => ($free ? theme.tokens.teal : theme.tokens.cardBorder)};
	background: ${({theme, $free}) => ($free ? theme.tokens.signalTint : theme.tokens.chip)};
`;

export const AvailabilityNext = styled.p`
	margin: 12px 0 0;
	font-size: 14px;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const AvailabilityNextValue = styled.strong`
	font-weight: 600;
	color: ${({theme}) => theme.tokens.ink};
`;
